import {
  CAP_ENEMIES,
  CAP_ENEMY_BULLETS,
  CAP_OBSTACLES,
  CAP_PICKUPS,
  CAP_PLAYER_BULLETS,
  PF_HH,
} from './constants';
import { EMPTY_INDEX, type DefIndex } from './defs';
import { EventRing } from './events';
import { DRONE_SLOTS, FEEL } from './FeelConstants';
import type { OwnedMod } from './mods';
import { DEFAULT_SECONDARY_ID } from './secondary';
import { emptyStats, type Modifier, type StatBlock } from './stats';

/** Une apparition programmée, en attente de son échéance. */
export interface QueuedSpawn {
  at: number;
  enemy: string;
  lane: number;
}

export interface RunProgress {
  level: number;
  xp: number;
  /** Montées de niveau en attente de choix de carte. Un compteur plutôt qu'un
   *  booléen : deux niveaux peuvent tomber dans le même tick sur une grosse
   *  vague, et aucun ne doit être perdu. */
  pendingLevelUps: number;
  owned: OwnedMod[];
  cells: number;
  /** Modificateurs permanents issus de la méta-progression, semés au début de
   *  la run. Ils entrent dans le calcul au même titre que les cartes. */
  baseMods: Modifier[];

  /* ── Compteurs de run ─────────────────────────────────────────────────
     Ils alimentent les JALONS DE MAÎTRISE, et c'est pour ça qu'ils sont
     incrémentés dans la simulation plutôt que reconstitués après coup : un
     jalon comme « terminer un monde sans bombe » ne peut pas se déduire d'un
     score final. Ce qui n'est pas compté sur le moment est perdu. */
  deaths: number;
  bombsUsed: number;
  /** Coups encaissés, y compris ceux annulés par un death-bomb. */
  hitsTaken: number;
  bossesKilled: number;
  worldsCleared: number;
  /** Statistiques EFFECTIVES, mises en cache. C'est ce que lit `playerSystem` —
   *  jamais les constantes `FEEL`, qui ne sont plus que des valeurs de base. */
  stats: StatBlock;
}

export interface RunState {
  levelId: string | null;
  /** Temps écoulé dans le niveau, en secondes. */
  t: number;
  nextBlock: number;
  activeWave: string | null;
  waveT: number;
  waveSpawn: number;
  queue: QueuedSpawn[];
  finished: boolean;
  /** Un bloc de boss a été activé : la fin du niveau dépend désormais de la
   *  disparition du boss, plus de l'horloge. */
  bossPending: boolean;
  /** Le boss est effectivement APPARU.
   *
   *  ⚠️ Indispensable, et son absence a produit une panne majeure : la fin de
   *  niveau se déclenche quand « le boss est attendu et l'écran est vide ». Or
   *  un bloc de boss s'ouvre précisément par cinq secondes de silence — écran
   *  vide, file d'attente vide — donc le niveau se terminait à l'instant même
   *  où le boss était annoncé, avant qu'il n'existe. Le joueur ne le voyait
   *  jamais et croyait l'avoir vaincu sans effort. */
  bossSpawned: boolean;
  /** `uid` du porteur du boss, pour la barre de vie du HUD. Zéro tant qu'aucun
   *  boss n'est en scène. Un uid et non un slot : les slots se recyclent. */
  bossUid: number;
  /** Coefficient de PV du MONDE en cours. Fourni par la couche application au
   *  démarrage d'un niveau (`startLevel`), plus lu depuis le `LevelDef` : dans
   *  la structure à deux étages, c'est le MONDE qui le porte, et tous ses
   *  niveaux le partagent. `levelSystem` le lit ici. */
  hpScale: number;
  /** Palier de difficulté de la partie (voir `src/sim/difficulty.ts`).
   *
   *  ⚠️ Il vit dans l'état de RUN et non dans les définitions, parce qu'il est
   *  choisi par le joueur avant de commencer et ne bouge plus ensuite. Le
   *  mettre dans `balance.json` en aurait fait un réglage global — donc partagé
   *  entre deux parties, donc faux dès qu'on veut comparer deux scores. */
  difficulty: number;
  /** Masque des contraintes volontaires choisies au briefing (voir
   *  `src/sim/constraints.ts`).
   *
   *  Même statut que `difficulty` : choisi avant de commencer, immuable ensuite,
   *  et il gouverne des ticks à venir — la cadence ennemie et le plafond de
   *  vies. Il entre donc dans l'empreinte de replay. */
  constraints: number;
  /** Mode SANS FIN : il n'y a pas de `LevelDef`, c'est `endlessSystem` qui peuple
   *  le monde procéduralement et durcit la partie avec le temps. Exclusif de
   *  `levelId` (l'un OU l'autre pilote le peuplement, jamais les deux). Le boss
   *  n'existe pas ici, et `finished` ne passe jamais à vrai : seule la mort du
   *  joueur termine la partie.
   *
   *  ⚠️ Gouverne des ticks à venir (l'apparition d'ennemis), donc entre dans
   *  l'empreinte de replay — mais sous garde `if (run.endless)`, à l'identique du
   *  bloc obstacles : une capacité neuve qu'aucun golden commité n'exerce ne doit
   *  pas décaler l'empreinte tant qu'elle est inactive. */
  endless: boolean;
  /** Instant (en secondes de `run.t`) de la prochaine impulsion de spawn du mode
   *  sans fin. Inerte hors mode sans fin. */
  endlessNext: number;

  /* ── Rang caché : difficulté dynamique INVISIBLE (voir `rankSystem.ts`) ──
     Un curseur qui monte quand le joueur domine et redescend quand il souffre,
     sans jamais s'afficher — d'où « caché ». */
  /** Multiplicateur de durcissement des ennemis, borné et lent. Part à 1
   *  (neutre). Écrit par `rankSystem` ; destiné à entrer dans `levelSystem.hpScale`
   *  (hook côté intégration). Gouverne les PV des ennemis à venir → haché. */
  rankMul: number;
  /** Photo de la performance cumulée au tick précédent (kills, frôlements, coups,
   *  morts, pondérés). `rankSystem` en dérive la variation RÉCENTE pour ne pas
   *  travailler sur les totaux d'une vie entière : un passage difficile doit
   *  faire redescendre le rang même après des centaines de kills. Influence le
   *  prochain tick (il en calcule le delta), donc haché. */
  rankPerf: number;
}
import { BulletPool } from './pools/BulletPool';
import { EnemyPool } from './pools/EnemyPool';
import { ObstaclePool } from './pools/ObstaclePool';
import { PickupPool } from './pools/PickupPool';
import { Rng } from './rng';
import { clampDifficulty, Difficulty, LIVES_DELTA } from './difficulty';
import { Bomb, clampBomb } from './bombs';
import { Constraint, livesCap, sanitizeMask } from './constraints';

/** Une frame d'entrées, consommée EN TÊTE de tick.
 *
 *  Volontairement pauvre : deux axes et deux booléens. La simulation ne sait
 *  rien du clavier, de la manette ni du tactile — c'est `platform/InputSource`
 *  qui traduit. C'est ce qui permet de rejouer une partie depuis un fichier
 *  d'inputs sans qu'aucun périphérique n'existe. */
export interface InputFrame {
  /** -1 .. 1 */
  moveX: number;
  /** -1 .. 1 */
  moveY: number;
  focus: boolean;
  bomb: boolean;
  /** Tir primaire. Vrai par défaut : le tir est automatique (PLAN.md §6.3), et
   *  le clic gauche s'y superpose au lieu de le remplacer. */
  fire: boolean;
  /** Arme secondaire — clic droit. Distincte de la bombe à terme (J7+). */
  secondary: boolean;
  /** ULTIME — touche dédiée. Optionnel : les nombreux littéraux `InputFrame`
   *  (tests, golden) n'ont pas à le porter ; absent vaut « non pressé ». */
  ulti?: boolean;
  /** Cible absolue du pointeur, en unités monde. `null` = pilotage au clavier.
   *
   *  ⚠️ Ce champ fait partie de l'entrée de SIMULATION, pas d'un état de
   *  périphérique : un replay à la souris doit rejouer la même trajectoire, ce
   *  qui exige que la position visée soit enregistrée tick par tick comme le
   *  reste. */
  aimX?: number | undefined;
  aimY?: number | undefined;
}

export function emptyInput(): InputFrame {
  return { moveX: 0, moveY: 0, focus: false, bomb: false, fire: true, secondary: false };
}

/** Le joueur reste un objet JS ordinaire, et c'est délibéré (PLAN.md §7.4).
 *  Le style SoA/typed-array est réservé aux projectiles, particules et
 *  collisions — là où il y a des milliers d'éléments homogènes. L'appliquer
 *  partout produirait un code illisible à six mois, et le risque de
 *  sur-ingénierie est ici plus probable que le risque de performance. */
export interface PlayerState {
  x: number;
  y: number;
  px: number;
  py: number;
  vx: number;
  vy: number;
  lives: number;
  /** Invulnérabilité restante, en secondes. */
  iframes: number;
  dead: boolean;
  /** Temps écoulé depuis la mort, en secondes. */
  deathT: number;
  fireCd: number;

  /** Bombes en stock. */
  bombs: number;
  /** État de la touche au tick précédent : la bombe se déclenche au front
   *  montant, jamais tant que la touche est maintenue. */
  bombHeld: boolean;
  /** ⚠️ Fenêtre de death-bomb restante, en secondes (PLAN.md §6.3).
   *
   *  Quand elle est > 0, le joueur vient d'être touché mais la mort n'est PAS
   *  encore actée : appuyer sur bombe l'annule. C'est ce qui transforme des
   *  dizaines de morts « j'ai réagi trop tard » en morts « j'ai réagi juste à
   *  temps », et ça ne s'explique jamais au joueur — ça se ressent. */
  deathBombT: number;

  /* ── Variante de bombe emportée (voir `src/sim/bombs.ts`) ─────────────
     Un ENTIER, pour la même raison que `secondary` juste plus bas : cet état
     entre dans l'empreinte de replay, et un index se hache en un mot machine.
     `Bomb.Eclat` (0) est la valeur offerte, celle d'un joueur qui n'a rien
     acheté — jamais `-1` : la touche bombe doit toujours faire quelque chose. */
  bomb: number;
  /** Temps restant de l'EFFET DE BORD de la dernière bombe, en secondes.
   *
   *  Un seul minuteur pour les trois variantes, parce qu'une seule bombe
   *  s'emporte par run : deux effets de nature différente ne peuvent jamais se
   *  chevaucher. Une deuxième bombe pendant l'effet le RELANCE, ce qui est le
   *  comportement attendu — on ne cumule pas deux colonnes, on prolonge. */
  bombFxT: number;
  /** Abscisse de la colonne du BRISANT, figée au déclenchement.
   *
   *  ⚠️ Figée, et non suivie sur le vaisseau. Une colonne qui suivrait le
   *  joueur deviendrait une arme de tir continu qu'on oriente en se déplaçant,
   *  c'est-à-dire une primaire bis pendant 2,2 s. Fixe, elle est une zone qu'on
   *  POSE : le choix se fait à l'instant de l'appui, ce qui est la seule
   *  décision qu'une bombe doit demander. */
  bombFxX: number;

  /* ── Arme secondaire (clic droit) ─────────────────────────────────────
     Index numérique et non chaîne : `PlayerState` entre dans l'empreinte de
     replay, et un entier est hachable en un mot machine là où une chaîne ne
     l'est pas. `-1` signifie « aucune arme équipée ». */
  secondary: number;
  /** Recharge restante, en secondes. */
  secondaryCd: number;
  /** État du bouton au tick précédent : déclenchement au FRONT MONTANT, sinon
   *  maintenir le clic viderait la recharge en boucle. */
  secondaryHeld: boolean;
  /** Volées restantes d'une rafale en cours. */
  secondaryShots: number;
  /** Délai avant la prochaine volée de la rafale. */
  secondaryShotT: number;
  /** Temps d'indisponibilité restant de chaque emplacement d'escorte, en
   *  secondes. Zéro = drone opérationnel.
   *
   *  Un tableau de longueur fixe plutôt qu'un pool : les emplacements sont au
   *  nombre de huit et ne naissent ni ne meurent, seul leur état change. */
  droneDown: number[];

  /* ── Bouclier joueur rechargeable ──────────────────────────────────────
     Une barrière qui encaisse un coup À LA PLACE d'une vie, puis se régénère
     après un délai sans être touché. La régénération et le rafraîchissement de
     `shieldMax` vivent dans `playerSystem` ; l'absorption dans
     `collisionSystem.hitPlayer`. */
  /** PV de bouclier courants. Zéro = plus de bouclier, le prochain coup coûte
   *  une vie comme avant. */
  shield: number;
  /** Capacité maximale. ⚠️ MIROIR de la stat `maxShield`, recopié à chaque tick
   *  par `playerSystem` : une carte de capacité ramassée en cours de partie
   *  relève le plafond immédiatement, sans hook d'application. Vaut ZÉRO pour un
   *  joueur sans amélioration — le bouclier est un choix de build, pas un
   *  cadeau, et la difficulté de base reste réglée comme elle l'a toujours été. */
  shieldMax: number;
  /** Délai restant avant que la régénération ne reprenne, en secondes. Remis à
   *  plein à chaque coup absorbé : un joueur sous le feu ne recharge pas. */
  shieldRegenCd: number;

  /* ── ULTIME (super-attaque chargée) ─────────────────────────────────────── */
  /** Jauge d'ulti, 0 → 1. Se remplit aux kills et frôlements ; pleine, la touche
   *  d'ulti déclenche la charge. */
  ulti: number;
  /** Charge restante avant la décharge, en secondes. > 0 = en train de charger. */
  ultiChargeT: number;
  /** Volées de décharge restantes. > 0 = l'ulti tire sa rafale monstrueuse. */
  ultiShots: number;
  /** Compte à rebours entre deux volées de la décharge, en secondes. */
  ultiShotT: number;
  /** État de la touche d'ulti au tick précédent : déclenchement au FRONT, comme
   *  la bombe — sinon la maintenir déclencherait en boucle. */
  ultiHeld: boolean;

  /* ── CHAUFFE DU CANON (tir manuel) ──────────────────────────────────────── */
  /** Chaleur du canon, 0 (froid) → 1 (surchauffe). Monte à chaque tir, descend
   *  au repos. */
  heat: number;
  /** En SURCHAUFFE : la cadence s'effondre jusqu'au refroidissement (hystérésis
   *  via HEAT_RECOVER). */
  overheated: boolean;
  /** Temps passé à TIRER en surchauffe, en secondes. À `OVERHEAT_JAM_SEC`, le
   *  canon s'enraye. Remis à zéro dès qu'on sort de surchauffe (ou qu'on relâche,
   *  puisqu'il n'avance qu'en tirant). */
  overheatT: number;
  /** Canon ENRAYÉ : le tir est bloqué NET (fumée) jusqu'au refroidissement complet
   *  (`HEAT_RECOVER`). La sanction d'avoir tiré trop longtemps en rouge vif. */
  jammed: boolean;
  /** SURALIMENTATION restante, en secondes (power-up Overdrive). Tant que > 0 : la
   *  cadence est doublée et le canon ne chauffe plus. Décompté chaque tick. */
  overdriveT: number;
}

/** Ce que le briefing choisit en plus du palier et de l'arme secondaire.
 *
 *  Un objet d'options plutôt que deux paramètres positionnels de plus : les
 *  deux sont des entiers, et `armPlayer(2, 3, 1, 5)` ne se relit pas. Les deux
 *  champs sont facultatifs — une partie sans bombe choisie ni contrainte est le
 *  cas normal, et c'est celui qui doit rester le plus court à écrire. */
export interface RunOptions {
  /** Variante de bombe (`Bomb.*`). Absent = celle déjà en place. */
  readonly bomb?: number | undefined;
  /** Masque de contraintes volontaires (`Constraint.*`). Absent = aucune. */
  readonly constraints?: number | undefined;
}

export interface OverflowCounters {
  enemyBullets: number;
  playerBullets: number;
  enemies: number;
  pickups: number;
  obstacles: number;
}

/**
 * L'état complet d'une partie. Sérialisable, hachable, rejouable.
 *
 * Rien ici ne dépend du temps réel : le seul « temps » est `tick`. C'est ce
 * qui rend la simulation exécutable dans Node aussi bien que dans le
 * navigateur, à 10 000 pas par seconde comme à 120.
 */
export class World {
  tick = 0;

  /** Aléa qui influe sur l'issue d'une partie. Seedé, sérialisé, rejoué. */
  readonly rng: Rng;
  /** Aléa purement cosmétique. Le séparer permet d'ajouter ou de retirer un
   *  effet visuel sans décaler le flux du gameplay — donc sans invalider les
   *  replays enregistrés ni les scores d'un leaderboard. */
  readonly rngVisual: Rng;

  readonly events = new EventRing(512);

  readonly eb = new BulletPool(CAP_ENEMY_BULLETS);
  readonly pb = new BulletPool(CAP_PLAYER_BULLETS);
  readonly enemies = new EnemyPool(CAP_ENEMIES);
  readonly pickups = new PickupPool(CAP_PICKUPS);
  /** Décor collisionnable (astéroïdes). Alloué une fois à sa capacité, comme les
   *  autres pools : zéro allocation en jeu. */
  readonly obstacles = new ObstaclePool(CAP_OBSTACLES);

  readonly player: PlayerState = {
    x: 0,
    y: -PF_HH + 220,
    px: 0,
    py: -PF_HH + 220,
    vx: 0,
    vy: 0,
    lives: 3,
    iframes: 0,
    dead: false,
    deathT: 0,
    fireCd: 0,
    bombs: FEEL.bombsAtStart,
    bombHeld: false,
    deathBombT: 0,
    bomb: Bomb.Eclat,
    bombFxT: 0,
    bombFxX: 0,
    secondary: -1,
    secondaryCd: 0,
    secondaryHeld: false,
    secondaryShots: 0,
    secondaryShotT: 0,
    droneDown: new Array<number>(DRONE_SLOTS.length).fill(0),
    // Bouclier vide au départ, capacité nulle : `armPlayer` le remplit à hauteur
    // de la stat `maxShield` (zéro sans amélioration), et `playerSystem` tient
    // ensuite `shieldMax` à jour tick par tick.
    shield: 0,
    shieldMax: 0,
    shieldRegenCd: 0,
    ulti: 0,
    ultiChargeT: 0,
    ultiShots: 0,
    ultiShotT: 0,
    ultiHeld: false,
    heat: 0,
    overheated: false,
    overheatT: 0,
    jammed: false,
    overdriveT: 0,
  };

  score = 0;
  grazeCount = 0;
  kills = 0;

  /** Refus d'allocation par saturation de pool. Toujours à zéro en jeu normal.
   *  Non nul = un pattern émet en boucle, ou une capacité est sous-dimensionnée.
   *  Affiché sur le banc de charge, surveillé en test. */
  readonly overflow: OverflowCounters = {
    enemyBullets: 0,
    playerBullets: 0,
    enemies: 0,
    pickups: 0,
    obstacles: 0,
  };

  /** Banc de charge du J1, conservé pour la mesure de performance. En jeu, le
   *  peuplement vient du `levelSystem` et de la timeline de contenu. */
  readonly spawn = { targetEnemies: 0, seedX: 0 };

  /** Progression en run : expérience, niveau, améliorations possédées. */
  readonly progress: RunProgress = {
    level: 1,
    xp: 0,
    pendingLevelUps: 0,
    owned: [],
    cells: 0,
    baseMods: [],
    stats: emptyStats(),
    deaths: 0,
    bombsUsed: 0,
    hitsTaken: 0,
    bossesKilled: 0,
    worldsCleared: 0,
  };

  /** État du déroulement de niveau. */
  readonly run: RunState = {
    levelId: null,
    t: 0,
    nextBlock: 0,
    activeWave: null,
    waveT: 0,
    waveSpawn: 0,
    queue: [],
    finished: false,
    bossPending: false,
    bossSpawned: false,
    bossUid: 0,
    hpScale: 1,
    difficulty: Difficulty.Operateur,
    constraints: Constraint.None,
    endless: false,
    endlessNext: 0,
    rankMul: 1,
    rankPerf: 0,
  };

  /** Le catalogue de contenu, INJECTÉ. La simulation ne l'importe jamais : la
   *  flèche de dépendance va de content vers sim, jamais l'inverse, et c'est ce
   *  qui permet de rejouer une partie headless ou d'éprouver un comportement
   *  avec un index fabriqué à la main. */
  defs: DefIndex;

  private readonly seed: number;

  constructor(seed = 0x1337, defs: DefIndex = EMPTY_INDEX) {
    this.defs = defs;
    this.player.secondary = defs.secondaryByI.get(DEFAULT_SECONDARY_ID) ?? -1;
    this.seed = seed | 0;
    this.rng = new Rng(this.seed);
    // Décalage arbitraire mais fixe : deux flux distincts issus d'une seule
    // graine, pour qu'un replay n'ait qu'un nombre à transporter.
    this.rngVisual = new Rng((this.seed ^ 0x5bf03635) | 0);
  }

  /**
   * Arme le joueur pour une nouvelle partie : palier de difficulté et vies.
   *
   * ⚠️ À appeler UNE FOIS, après `reset()` et après `syncStats()`, avant
   * `startLevel()`. L'ordre n'est pas décoratif :
   *   — `reset()` remet les vies à la valeur de base et effacerait le calcul ;
   *   — `syncStats()` doit avoir tourné, sinon `stats.maxLives` ne contient pas
   *     encore les bonus permanents de méta-progression.
   *
   * ⚠️ Cette méthode est SÉPARÉE de `syncStats()` à dessein, alors qu'elle lit
   * ses résultats. `syncStats()` est rappelée à chaque carte ramassée en cours
   * de partie ; y placer l'attribution des vies les rendrait pleines à chaque
   * montée de niveau, ce qui supprimerait purement et simplement la mort.
   *
   * Elle corrige au passage un défaut discret : `maxLives` était une
   * statistique calculée, plafonnée, modifiable par la méta-progression — et
   * que RIEN ne lisait. Le nœud « +1 vie » s'achetait et n'avait aucun effet.
   */
  armPlayer(difficulty: number, secondary?: number, options?: RunOptions): void {
    this.run.difficulty = clampDifficulty(difficulty);
    /* Arme secondaire choisie au briefing. Un index négatif ou absent laisse
       celle posée par `reset()` — la secondaire par défaut. Le clic droit doit
       toujours faire quelque chose, y compris pour un joueur qui n'a rien
       débloqué et n'a jamais vu d'écran de choix. */
    if (secondary !== undefined && secondary >= 0 && secondary < this.defs.secondaries.length) {
      this.player.secondary = secondary;
    }
    /* Bombe emportée. Même règle que la secondaire : une entrée absente ou
       aberrante laisse ÉCLAT, jamais « aucune bombe ». La touche doit répondre
       même à un joueur qui n'a rien acheté. */
    this.player.bomb = clampBomb(options?.bomb ?? this.player.bomb);
    this.player.bombFxT = 0;
    this.player.bombFxX = 0;

    const base = this.progress.stats.maxLives;
    const lives = base + LIVES_DELTA[clampDifficulty(difficulty)]!;
    // Au moins une vie, quoi qu'il arrive : un palier qui en retire plus que la
    // base donnerait une partie terminée avant d'avoir commencé.
    this.player.lives = lives > 1 ? Math.floor(lives) : 1;

    /* ⚠️ Les bombes viennent des STATISTIQUES, plus de `FEEL.bombsAtStart`.

       C'était la cinquième occurrence de la même panne dans ce projet :
       `maxBombs` était une statistique calculée, plafonnée, modifiable par les
       cartes et par l'arbre de méta — et que RIEN ne lisait. `reset()` posait
       deux bombes en dur. Les cartes « +1 bombe » du pool étaient donc inertes,
       le bonus de l'AEGIS invisible, et surtout le « plus aucune bombe, jamais »
       du FANAL — sa seule contrepartie — ne se produisait pas : le joueur
       payait un vaisseau bridé et gardait ses bombes.

       Zéro est une valeur LÉGITIME ici, contrairement aux vies. Le FANAL vend
       exactement ça, et le borner à un le viderait de son sens. */
    const bombs = this.progress.stats.maxBombs;
    this.player.bombs = bombs > 0 ? Math.floor(bombs) : 0;

    /* Bouclier armé À PLEIN, au niveau de la stat `maxShield` (déjà composée par
       `syncStats`, appelé avant — même précondition que les vies et les bombes).
       Zéro sans amélioration : le joueur de série démarre sans bouclier, exactement
       comme aujourd'hui. `playerSystem` maintient ensuite `shieldMax` et régénère
       `shield`. */
    const shieldMax = this.progress.stats.maxShield;
    this.player.shieldMax = shieldMax > 0 ? shieldMax : 0;
    this.player.shield = this.player.shieldMax;
    this.player.shieldRegenCd = 0;

    /* ⚠️ Les contraintes volontaires s'appliquent EN DERNIER, après le calcul
       des vies. « Coque de verre » doit écraser à la fois le +2 de la Recrue et
       toute la branche PV de l'arbre de méta ; l'appliquer avant se ferait
       silencieusement annuler par la ligne ci-dessus, et le joueur paierait
       +80 % de crédits pour une contrainte inexistante. */
    const mask = sanitizeMask(options?.constraints ?? Constraint.None);
    this.run.constraints = mask;
    const cap = livesCap(mask);
    if (cap > 0 && this.player.lives > cap) this.player.lives = cap;
  }

  /**
   * Démarre un niveau du catalogue.
   *
   * `fromBlock` permet de commencer AU MILIEU de la partition — l'entraînement
   * s'en sert pour reprendre un combat de boss sans retraverser deux minutes de
   * vagues. L'horloge est avancée à l'instant du bloc visé plutôt que remise à
   * zéro : `levelSystem` compare `b.at` à `run.t`, et repartir de zéro
   * rejouerait toute la timeline depuis le début.
   */
  startLevel(id: string, hpScale = 1, fromBlock = 0): void {
    const level = this.defs.levels.get(id);
    const bloc = level?.blocks[fromBlock];
    this.run.levelId = id;
    this.run.hpScale = hpScale;
    this.run.t = bloc ? bloc.at : 0;
    this.run.nextBlock = bloc ? fromBlock : 0;
    this.run.activeWave = null;
    this.run.waveT = 0;
    this.run.waveSpawn = 0;
    this.run.queue.length = 0;
    this.run.finished = false;
    this.run.bossPending = false;
    this.run.bossSpawned = false;
    this.run.bossUid = 0;
    // Un niveau et le mode sans fin sont EXCLUSIFS : démarrer un niveau éteint le
    // mode sans fin, sinon les deux peupleraient le monde en même temps.
    this.run.endless = false;
    this.run.endlessNext = 0;
  }

  /**
   * Démarre le mode SANS FIN.
   *
   * Pas de `LevelDef`, pas de boss, pas de fin par horloge : `endlessSystem`
   * peuple le monde procéduralement et durcit la partie avec le temps écoulé.
   * Seule la mort du joueur y met fin. Le coefficient de PV part à 1 et grimpe
   * ensuite, piloté par le temps dans `endlessSystem`.
   */
  startEndless(): void {
    this.run.levelId = null;
    this.run.activeWave = null;
    this.run.t = 0;
    this.run.nextBlock = 0;
    this.run.waveT = 0;
    this.run.waveSpawn = 0;
    this.run.queue.length = 0;
    this.run.finished = false;
    this.run.bossPending = false;
    this.run.bossSpawned = false;
    this.run.bossUid = 0;
    this.run.hpScale = 1;
    this.run.endless = true;
    // Une courte accalmie d'entrée : le joueur voit le décor avant la première
    // vague. La montée en difficulté commence ensuite.
    this.run.endlessNext = 1.5;
  }

  reset(seed = this.seed): void {
    this.tick = 0;
    this.run.t = 0;
    this.run.nextBlock = 0;
    this.run.activeWave = null;
    this.run.waveT = 0;
    this.run.waveSpawn = 0;
    this.run.queue.length = 0;
    this.run.finished = false;
    this.run.bossPending = false;
    this.run.bossSpawned = false;
    this.run.bossUid = 0;
    this.run.hpScale = 1;
    /* ⚠️ Les contraintes tombent à la remise à zéro, comme l'arme secondaire et
       à la différence du palier de difficulté. La raison est le sens de la
       panne : un palier oublié se voit au premier ennemi, une contrainte
       oubliée fait démarrer la partie suivante à une seule vie sans que rien ne
       l'annonce. `armPlayer` les repose ensuite si le joueur les a rechoisies. */
    this.run.constraints = Constraint.None;
    this.run.endless = false;
    this.run.endlessNext = 0;
    // Rang caché : on repart NEUTRE. Le laisser traîner ferait démarrer la partie
    // suivante déjà durcie (ou déjà adoucie) par les exploits de la précédente.
    this.run.rankMul = 1;
    this.run.rankPerf = 0;
    this.rng.state = seed | 0;
    this.rngVisual.state = (seed ^ 0x5bf03635) | 0;
    this.events.clear();
    this.eb.clear();
    this.pb.clear();
    this.enemies.clear();
    this.pickups.clear();
    this.obstacles.clear();

    this.progress.level = 1;
    this.progress.xp = 0;
    this.progress.pendingLevelUps = 0;
    this.progress.owned.length = 0;
    this.progress.cells = 0;
    this.progress.baseMods.length = 0;
    this.progress.stats = emptyStats();
    this.progress.deaths = 0;
    this.progress.bombsUsed = 0;
    this.progress.hitsTaken = 0;
    this.progress.bossesKilled = 0;
    this.progress.worldsCleared = 0;

    const p = this.player;
    p.x = 0;
    p.y = -PF_HH + 220;
    p.px = p.x;
    p.py = p.y;
    p.vx = 0;
    p.vy = 0;
    p.lives = 3;
    p.iframes = 0;
    p.dead = false;
    p.deathT = 0;
    p.fireCd = 0;
    p.bombs = FEEL.bombsAtStart;
    p.bombHeld = false;
    p.deathBombT = 0;
    // Bombe de départ : celle qui est offerte. Un effet de bord encore en cours
    // à la fin d'une partie ne doit rien laisser traîner dans la suivante —
    // c'est la même famille d'oubli que le drone resté hors service.
    p.bomb = Bomb.Eclat;
    p.bombFxT = 0;
    p.bombFxX = 0;
    // Arme de départ résolue par IDENTIFIANT, jamais par index 0 : l'ordre du
    // registre dépend des chemins de fichiers, et renommer une arme changerait
    // silencieusement l'équipement initial.
    p.secondary = this.defs.secondaryByI.get(DEFAULT_SECONDARY_ID) ?? -1;
    p.secondaryCd = 0;
    p.secondaryHeld = false;
    p.secondaryShots = 0;
    p.secondaryShotT = 0;
    // ⚠️ Remise à zéro OBLIGATOIRE : un drone hors service à la fin d'une partie
    // le resterait au début de la suivante, et le joueur commencerait avec un
    // projectile en moins sans la moindre explication.
    p.droneDown.fill(0);
    // Bouclier remis à plat : la capacité effective sera reposée par `armPlayer`
    // à partir des statistiques recomposées. `emptyStats()` ci-dessus a remis
    // `maxShield` à zéro, donc un bouclier restant serait un reliquat incohérent.
    p.shield = 0;
    p.shieldMax = 0;
    p.shieldRegenCd = 0;
    // Ulti remise à plat : une jauge à demi pleine ou une décharge en cours
    // héritée de la partie précédente n'a aucun sens.
    p.ulti = 0;
    p.ultiChargeT = 0;
    p.ultiShots = 0;
    p.ultiShotT = 0;
    p.ultiHeld = false;
    p.heat = 0;
    p.overheated = false;
    p.overheatT = 0;
    p.jammed = false;
    p.overdriveT = 0;

    this.score = 0;
    this.grazeCount = 0;
    this.kills = 0;
    this.overflow.enemyBullets = 0;
    this.overflow.playerBullets = 0;
    this.overflow.enemies = 0;
    this.overflow.pickups = 0;
    this.overflow.obstacles = 0;
  }

  /** Mémorise la position d'avant le tick, pour l'interpolation au rendu et
   *  pour la collision balayée. Appelé en tête de `step`. */
  savePrev(): void {
    const p = this.player;
    p.px = p.x;
    p.py = p.y;
    this.eb.savePrev();
    this.pb.savePrev();
    this.enemies.savePrev();
    this.pickups.savePrev();
    this.obstacles.savePrev();
  }
}
